// src/components/AddressInput.js
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { MapPin, Search, Loader } from 'lucide-react';

const AddressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const InputRow = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const AddressField = styled.input`
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.1)'};
  padding: 0.5rem 5rem 0.5rem 0;
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  font-size: 1rem;
  font-family: ${props => props.theme?.fonts?.body || "'Inter', sans-serif"};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-bottom-color: ${props => props.theme?.colors?.accent || '#800000'};
  }

  &::placeholder {
    color: ${props => `${props.theme?.colors?.text}90` || 'rgba(255, 255, 255, 0.5)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  position: absolute;
  right: 0;
  bottom: 0.5rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const IconButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
  color: ${props => props.theme?.colors?.accent || '#800000'};
  padding: 0.4rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  min-width: 32px;
  min-height: 32px;

  &:hover:not(:disabled) {
    background: ${props => `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CoordinateDisplay = styled.div`
  font-size: 0.75rem;
  color: ${props => `${props.theme?.colors?.text}70`};
  padding: 0.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .coords {
    font-family: monospace;
  }

  .success {
    color: #4CAF50;
  }
`;

const ErrorMessage = styled.div`
  font-size: 0.75rem;
  color: #f44336;
  padding: 0.25rem 0;
`;

const AddressInput = ({ address, onAddressChange, onLocationSelect, onMapOpen, theme }) => {
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  
  // ✅ NEW: Track if this is initial mount
  const isInitialMount = useRef(true);
  // ✅ NEW: Track if user manually triggered geocoding
  const userTriggeredGeocode = useRef(false);

  // ✅ NEW: Mark initial mount as complete after first render
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  // Debounced geocoding function
  useEffect(() => {
    setError(null);
    
    // ✅ NEW: Don't auto-geocode on initial mount
    if (isInitialMount.current) {
      console.log('⏭️ Skipping geocoding on initial mount');
      return;
    }
    
    // Don't geocode if address is empty or too short
    if (!address || address.length < 5) {
      setCoordinates(null);
      return;
    }

    // ✅ NEW: Only auto-geocode if user manually triggered it
    if (!userTriggeredGeocode.current) {
      console.log('⏭️ Skipping auto-geocoding, user must click search button');
      return;
    }

    // Debounce geocoding to avoid excessive API calls
    const timeoutId = setTimeout(() => {
      geocodeAddress(address);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [address]);

  const geocodeAddress = async (addressText) => {
    try {
      setConverting(true);
      setError(null);

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(addressText)}&` +
        `format=json&` +
        `limit=1&` +
        `addressdetails=1`,
        {
          headers: {
            'User-Agent': 'KalKode-Shop-App'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Geocoding service unavailable');
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        const coords = {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon)
        };

        const city = result.address?.city ||
                     result.address?.town ||
                     result.address?.village ||
                     result.address?.county ||
                     null;
        const state   = result.address?.state   || null;
        const country = result.address?.country || null;

        setCoordinates(coords);

        // ✅ CHANGED: Only call onLocationSelect if user triggered it
        if (onLocationSelect && userTriggeredGeocode.current) {
          console.log('✅ Calling onLocationSelect (user-triggered)');
          onLocationSelect({
            address: addressText,
            coordinates: coords,
            displayName: result.display_name,
            city,
            state,
            country,
          });
        }
        
        // ✅ NEW: Reset trigger flag
        userTriggeredGeocode.current = false;
      } else {
        setCoordinates(null);
        if (addressText.length > 15) {
          setError('Location not found. Try being more specific or use the map.');
        }
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      setError('Unable to find location. Use the map button to select manually.');
      setCoordinates(null);
    } finally {
      setConverting(false);
    }
  };

  const handleManualGeocode = () => {
    if (address && address.trim()) {
      // ✅ NEW: Set flag that user manually triggered this
      userTriggeredGeocode.current = true;
      geocodeAddress(address.trim());
    }
  };

  return (
    <AddressContainer>
      <InputRow>
        <AddressField
          value={address || ''}
          onChange={(e) => onAddressChange(e.target.value)}
          placeholder="Enter address or select on map"
          disabled={converting}
          theme={theme}
        />
        
        <ButtonGroup>
          <IconButton 
            onClick={handleManualGeocode}
            title="Find coordinates"
            disabled={converting || !address || address.length < 5}
            theme={theme}
          >
            {converting ? <Loader size={16} className="spinning" /> : <Search size={16} />}
          </IconButton>

          <IconButton 
            onClick={onMapOpen} 
            title="Select on map"
            theme={theme}
          >
            <MapPin size={16} />
          </IconButton>
        </ButtonGroup>
      </InputRow>

      {coordinates && (
        <CoordinateDisplay theme={theme}>
          <span className="success">✓ Located:</span>
          <span className="coords">
            {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
          </span>
        </CoordinateDisplay>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <style>
        {`
          .spinning {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </AddressContainer>
  );
};

export default AddressInput;